import React, { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Users, Loader2, AlertCircle, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import { laborScheduleService } from '../services/laborScheduleService';
import { teamService } from '../../empleados/services/teamService';

/**
 * Asignación de patrón a un equipo completo (líder + miembros activos).
 * El backend cierra la asignación activa previa de cada empleado antes de crear
 * la nueva, evitando asignaciones dobles activas.
 */
export function LaborTeamPatternSetup({
  factoryId,
  calendarId,
  patterns = [],
  loadingPatterns = false,
  onAfterChange,
}) {
  const [teamId, setTeamId] = useState('');
  const [assignPatternId, setAssignPatternId] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState('');
  const [message, setMessage] = useState(null);
  const [showMembers, setShowMembers] = useState(false);

  // Cargar equipos filtrados por fábrica
  const { data: allTeams = [], isLoading: loadingTeams } = useQuery({
    queryKey: ['labor-teams-for-factory', factoryId],
    queryFn: () => teamService.getTeams(),
    enabled: Boolean(factoryId),
    staleTime: 60_000,
    select: (teams) =>
      teams
        .filter((t) => Number(t.factory_id) === Number(factoryId))
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
  });

  // Cargar miembros del equipo seleccionado (para previsualización)
  const { data: teamMembers = [], isLoading: loadingMembers } = useQuery({
    queryKey: ['labor-team-members-setup', teamId],
    queryFn: () => teamService.getTeamMembers(teamId),
    enabled: Boolean(teamId),
    staleTime: 30_000,
  });

  // Limpiar mensaje al cambiar selección
  useEffect(() => {
    setMessage(null);
  }, [teamId, assignPatternId, startDate]);

  const selectedTeam = allTeams.find((t) => String(t.id) === String(teamId));

  // Contar personas afectadas: líder (si no está ya en members) + members
  const memberIds = new Set(teamMembers.map((m) => m.employee_id));
  const leaderIsAlsoMember = selectedTeam?.leader_id && memberIds.has(selectedTeam.leader_id);
  const totalAffected = teamMembers.length + (selectedTeam?.leader_id && !leaderIsAlsoMember ? 1 : 0);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!teamId) throw new Error('Selecciona un equipo.');
      if (!assignPatternId) throw new Error('Selecciona un patrón de trabajo.');
      if (!calendarId) throw new Error('No hay calendario de planta para este año. Créalo en la vista Fábrica.');
      if (!startDate) throw new Error('Indica la fecha de inicio.');

      return laborScheduleService.assignPatternToTeam(teamId, {
        patternId: Number(assignPatternId),
        calendarId: Number(calendarId),
        startDate,
        endDate: endDate || null,
      });
    },
    onSuccess: (result) => {
      const pat = patterns.find((p) => String(p.id) === String(assignPatternId));
      setMessage({
        type: 'success',
        result,
        text: `Patrón «${pat?.name || assignPatternId}» asignado a ${result.assigned} empleado${result.assigned !== 1 ? 's' : ''} del equipo «${result.team_name}».${
          result.skipped > 0 ? ` ${result.skipped} omitido${result.skipped !== 1 ? 's' : ''} (ya tenían este patrón).` : ''
        }`,
      });
      onAfterChange?.();
    },
    onError: (err) => {
      setMessage({
        type: 'error',
        text: err?.message || 'No se pudo asignar el patrón al equipo.',
      });
    },
  });

  const canAssign =
    Boolean(teamId) &&
    Boolean(factoryId) &&
    Boolean(calendarId) &&
    Boolean(assignPatternId) &&
    Boolean(startDate) &&
    patterns.length > 0 &&
    !mutation.isPending;

  return (
    <div className="mb-4 rounded-lg border border-indigo-700/60 bg-gray-800/80 p-3">
      <div className="flex flex-wrap items-end gap-3">
        {/* Icono + label */}
        <span className="text-sm text-gray-400 flex items-center gap-1.5 w-full sm:w-auto">
          <Users className="w-4 h-4 shrink-0 text-indigo-400" />
          Asignar patrón a equipo:
        </span>

        {/* Selector de equipo */}
        <div className="min-w-[180px] flex-1 max-w-xs">
          <label htmlFor="team-assign-team" className="block text-xs text-gray-400 mb-1">
            Equipo
          </label>
          <select
            id="team-assign-team"
            value={teamId}
            onChange={(e) => {
              setTeamId(e.target.value);
              setShowMembers(false);
            }}
            disabled={loadingTeams || allTeams.length === 0}
            className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
          >
            <option value="">
              {loadingTeams ? 'Cargando…' : allTeams.length === 0 ? 'Sin equipos en esta fábrica' : 'Seleccionar equipo…'}
            </option>
            {allTeams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>

        {/* Selector de patrón */}
        <div className="min-w-[180px] flex-1 max-w-xs">
          <label htmlFor="team-assign-pattern" className="block text-xs text-gray-400 mb-1">
            Patrón
          </label>
          <select
            id="team-assign-pattern"
            value={assignPatternId}
            onChange={(e) => setAssignPatternId(e.target.value)}
            disabled={loadingPatterns || patterns.length === 0}
            className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
          >
            <option value="">
              {loadingPatterns
                ? 'Cargando patrones…'
                : patterns.length === 0
                  ? 'Sin patrones (crea uno en vista Patrón)'
                  : 'Seleccionar patrón…'}
            </option>
            {patterns.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        {/* Fecha inicio */}
        <div>
          <label htmlFor="team-assign-start" className="block text-xs text-gray-400 mb-1">
            Inicio
          </label>
          <input
            id="team-assign-start"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
          />
        </div>

        {/* Fecha fin */}
        <div>
          <label htmlFor="team-assign-end" className="block text-xs text-gray-400 mb-1">
            Fin (opcional)
          </label>
          <input
            id="team-assign-end"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            min={startDate || undefined}
            className="bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
          />
        </div>

        {/* Botón asignar */}
        <button
          type="button"
          onClick={() => {
            setMessage(null);
            mutation.mutate();
          }}
          disabled={!canAssign}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium disabled:opacity-50 transition-colors"
        >
          {mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
          Asignar a equipo
        </button>
      </div>

      {/* Preview de miembros afectados */}
      {teamId && !loadingMembers && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setShowMembers((v) => !v)}
            className="flex items-center gap-1.5 text-xs text-indigo-300 hover:text-indigo-200 transition-colors"
          >
            {showMembers ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            {totalAffected} empleado{totalAffected !== 1 ? 's' : ''} serán afectados
            {selectedTeam?.leader_id && !leaderIsAlsoMember ? ' (incluye líder)' : ''}
          </button>

          {showMembers && (
            <div className="mt-2 max-h-40 overflow-y-auto rounded-md border border-gray-700 bg-gray-900/50 p-2 grid grid-cols-2 sm:grid-cols-3 gap-1">
              {/* Líder */}
              {selectedTeam?.leader && !leaderIsAlsoMember && (
                <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-indigo-900/30 border border-indigo-700/40">
                  <span className="text-xs text-indigo-300 font-medium truncate">
                    {selectedTeam.leader.first_name} {selectedTeam.leader.last_name}
                  </span>
                  <span className="text-xs text-indigo-500 ml-auto shrink-0">líder</span>
                </div>
              )}
              {teamMembers.map((m) => (
                <div
                  key={m.id}
                  className="flex items-center gap-1.5 px-2 py-1 rounded bg-gray-800 border border-gray-700"
                >
                  <span className="text-xs text-gray-300 truncate">
                    {m.employee?.first_name || ''} {m.employee?.last_name || `#${m.employee_id}`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Alertas de configuración */}
      {!factoryId && (
        <p className="mt-2 text-xs text-amber-400 flex items-center gap-1">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
          Selecciona una fábrica en el header para cargar equipos y patrones.
        </p>
      )}
      {factoryId && !calendarId && !loadingPatterns && (
        <p className="mt-2 text-xs text-amber-400 flex items-center gap-1">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
          No hay calendario de planta para este año. Créalo en la vista Fábrica.
        </p>
      )}

      {/* Resultado */}
      {message?.type === 'success' && (
        <div className="mt-2 flex items-start gap-1.5 text-sm text-emerald-400">
          <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{message.text}</span>
        </div>
      )}
      {message?.type === 'success' && message.result?.errors?.length > 0 && (
        <details className="mt-1 text-xs text-amber-400">
          <summary className="cursor-pointer">
            {message.result.errors.length} error{message.result.errors.length !== 1 ? 'es' : ''} parciales
          </summary>
          <ul className="mt-1 space-y-0.5 pl-3 list-disc">
            {message.result.errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </details>
      )}
      {message?.type === 'error' && (
        <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
          {message.text}
        </p>
      )}
    </div>
  );
}
